import React, { useState } from 'react';
import {
  Typography,
  Box,
  IconButton,
  TextField,
  Button,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { marked } from 'marked';

const QuestionItem = ({ question, topicId, onUpdate, onDelete, onAttemptUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({
    id: question.id,
    questionText: question.questionText,
    answerText: question.answerText || '',
  });
  const [error, setError] = useState('');
  const [previewMode, setPreviewMode] = useState('edit'); // 'edit' or 'preview'

  const handleEdit = () => {
    setEditMode(true);
    setPreviewMode('edit');
    setEditData({
      id: question.id,
      questionText: question.questionText,
      answerText: question.answerText || '',
    });
  };

  const handleSave = async () => {
    try {
      await onUpdate(editData);
      setEditMode(false);
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setPreviewMode('edit');
    setError('');
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this question? This cannot be undone.')) {
      try {
        await onDelete();
      } catch (error) {
        setError(error.message);
      }
    }
  };

  const handleMarkAttempt = async (isCorrect) => {
    try {
      const currentAttempts = question.attempts || [];
      const newAttempts = [...currentAttempts, isCorrect].slice(-5);
      await onAttemptUpdate(question.id, newAttempts);
      setError('');
    } catch (error) {
      setError(error.message);
    }
  };

  const renderAttemptIcons = () => {
    const attempts = question.attempts || [];
    const icons = [];
    
    // Get last 5 attempts in reverse order (most recent first)
    const lastFiveAttempts = attempts.slice(-5).reverse();
    
    // Render actual attempts (most recent on left)
    for (let i = 0; i < lastFiveAttempts.length; i++) {
      icons.push(
        <Tooltip key={`attempt-${i}`}  arrow>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: lastFiveAttempts[i] ? '#4caf50' : '#f44336',
              display: 'inline-block',
              mx: 0.25,
            }}
          />
        </Tooltip>
      );
    }
    
    // Fill remaining slots with grey circles (on the right)
    for (let i = lastFiveAttempts.length; i < 5; i++) {
      icons.push(
        <Tooltip key={`empty-${i}`} arrow>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor: '#bdbdbd',
              display: 'inline-block',
              mx: 0.25,
            }}
          />
        </Tooltip>
      );
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }}>
        {icons}
      </Box>
    );
  };

  const renderMarkdownAnswer = (answer) => {
    const htmlContent = marked(answer);
    return (
      <Box
        className="markdown-content"
        sx={{
          '& *': {
            margin: 0,
            padding: 0,
          }
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  const renderMarkdownPreview = () => {
    const htmlContent = marked(editData.answerText);
    return (
      <Box
        className="markdown-content"
        sx={{
          p: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          minHeight: '200px',
          overflow: 'auto'
        }}
        dangerouslySetInnerHTML={{ __html: htmlContent }}
      />
    );
  };

  return (
    <Box sx={{ mb: 1 }}>
      {editMode ? (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600, color: 'text.primary', fontSize: '1rem' }}>
            Edit Question
          </Typography>
          <TextField
            fullWidth
            label="Question"
            value={editData.questionText}
            onChange={(e) => setEditData({ ...editData, questionText: e.target.value })}
            sx={{ mb: 1 }}
            multiline
            rows={2}
          />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Answer
              </Typography>
              <ToggleButtonGroup
                value={previewMode}
                exclusive
                onChange={(e, newMode) => newMode && setPreviewMode(newMode)}
                size="small"
              >
                <ToggleButton value="edit" sx={{ px: 2, py: 0.5 }}>
                  <EditIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Edit
                </ToggleButton>
                <ToggleButton value="preview" sx={{ px: 2, py: 0.5 }}>
                  <VisibilityIcon sx={{ fontSize: 16, mr: 0.5 }} />
                  Preview
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {previewMode === 'edit' ? (
          <TextField
            fullWidth
            label="Answer"
            value={editData.answerText}
            onChange={(e) => setEditData({ ...editData, answerText: e.target.value })}
            sx={{ mb: 1 }}
            multiline
            rows={4}
            placeholder="Enter your answer here. You can use markdown-style formatting for code blocks."
          />
            ) : (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                  Preview of your answer:
                </Typography>
                {renderMarkdownPreview()}
              </Box>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 1, borderRadius: 1 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="contained" 
              size="small"
              onClick={handleSave}
              sx={{ px: 2, fontWeight: 600 }}
            >
              Save Changes
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={handleCancel}
              sx={{ px: 2, fontWeight: 600 }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      ) : (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
              <Typography
                variant="body1"
                component="div"
                sx={{
                  fontWeight: 500,
                  color: question.answerText ? '#1976d2' : 'text.primary',
                  lineHeight: 1.4,
                  fontSize: '0.98rem',
                  flex: 1,
                  cursor: question.answerText ? 'pointer' : 'default',
                  userSelect: 'none',
                  '&:hover': question.answerText ? { textDecoration: 'underline' } : {},
                  transition: 'color 0.2s',
                  background: 'none',
                  boxShadow: 'none',
                  border: 'none',
                  borderRadius: 0,
                  p: 0,
                  m: 0
                }}
                onClick={e => {
                  e.stopPropagation();
                  if (question.answerText) setExpanded(prev => !prev);
                }}
              >
                {question.questionText}
              </Typography>
              {renderAttemptIcons()}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
              <Tooltip arrow>
                <IconButton 
                  size="small" 
                  onClick={() => handleMarkAttempt(true)}
                  className="question-action-btn"
                  sx={{ 
                    bgcolor: '#4caf50',
                    color: 'white',
                    '&:hover': { bgcolor: '#45a049' },
                    p: 0.5
                  }}
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip arrow>
                <IconButton 
                  size="small" 
                  onClick={() => handleMarkAttempt(false)}
                  className="question-action-btn"
                  sx={{ 
                    bgcolor: '#f44336',
                    color: 'white',
                    '&:hover': { bgcolor: '#da190b' },
                    p: 0.5
                  }}
                >
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton 
                size="small" 
                onClick={handleEdit}
                className="question-action-btn"
                sx={{ 
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' },
                  p: 0.5
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                onClick={handleDelete} 
                color="error"
                className="question-action-btn"
                sx={{ 
                  bgcolor: 'error.light',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.main' },
                  p: 0.5
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
          {question.answerText && expanded && (
            <Box sx={{ 
              p: 1.5, 
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              mt: 1,
              width: '100%',
              boxSizing: 'border-box',
              boxShadow: 'none',
              mb: 0.5,
            }}>
              {renderMarkdownAnswer(question.answerText)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QuestionItem; 